import {
	Alert,
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	DateRangePickerField,
	Form,
	SelectInputField,
	SubmissionError,
	TextInputField,
	CheckboxInputField,
	EmptyNumberValidator,
	ErrorMessage,
	withServerErrors,
	validateFormValues,
} from "@sembark-travel/ui-form"
import { formatDate, startOf } from "@sembark-travel/datetime-utils"
import { useState } from "react"
import { useXHR } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { IHotel } from "../Hotels"
import {
	DJUBO_ACTIONS,
	DJUBO_WEEKDAYS,
	TDjuboAction,
	TDjuboWebhookConfig,
	TDjuboWebhookResponse,
	TDjuboWeekday,
	sendDjuboWebhook,
	useDjuboIntegration,
	weekdaysToDjuboBitmask,
} from "./store"

function toApiDate(date: Date | string): string {
	return typeof date === "string" ? date : formatDate(date, "YYYY-MM-DD")
}

/**
 * Every form on this page live in the same document, and the form library
 * derive each field's DOM id from its name. So each form prefix its field
 * names, otherwise a label would focus the matching field of the first form.
 */
function dateRangeValidators(prefix: string) {
	return {
		[`${prefix}start_date`]: Validator.date().required(
			"Please select a start date"
		),
		[`${prefix}end_date`]: Validator.date()
			.required("Please select an end date")
			.min(
				Validator.ref(`${prefix}start_date`),
				"End date must be after the start date"
			),
	}
}

/**
 * Imitate the Djubo channel manager for a hotel by sending the same webhook
 * calls Djubo would send. Only for testing the integration.
 */
export function DjuboSimulator({ hotel }: { hotel: IHotel }) {
	const { webhookConfig, isFetching, error } = useDjuboIntegration()

	if (isFetching) {
		return <Text>Loading Djubo integration...</Text>
	}

	if (error || !webhookConfig) {
		return (
			<Alert status="warning" title="Djubo integration missing">
				This hotel is connected to Djubo, but there is no active Djubo
				integration for your organization. Please create one from the
				Integrations settings before using the simulator.
			</Alert>
		)
	}

	return (
		<Stack gap="6">
			<Alert status="info" title="Sandbox">
				Every form below send the same webhook request that Djubo would send to
				Sembark. Use it to verify the integration without a real Djubo account.
			</Alert>
			<WebhookConfigDetails config={webhookConfig} hotel={hotel} />
			<Stack gap="2">
				<Heading as="h5">Update ARI</Heading>
				<Text fontSize="sm" color="muted">
					These are queued and applied by a worker, so they answer with an
					acknowledgement rather than the updated data.
				</Text>
			</Stack>
			{/* no `gap` here: it switches the Grid to no-gutters and drop the
			Col padding, which push the columns past 100% and stack them */}
			<Grid>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<UpdateRoomRatesForm hotel={hotel} config={webhookConfig} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<UpdateRoomInventoryForm hotel={hotel} config={webhookConfig} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<StopSellByRatePlanForm hotel={hotel} config={webhookConfig} />
				</Col>
			</Grid>
			<Stack gap="2">
				<Heading as="h5">Fetch ARI</Heading>
				<Text fontSize="sm" color="muted">
					These answer straight away with what Sembark currently hold, so use
					them to confirm an update actually landed.
				</Text>
			</Stack>
			<Grid>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<GetRoomRatesForm hotel={hotel} config={webhookConfig} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<GetRoomInventoryForm hotel={hotel} config={webhookConfig} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<GetRoomAndRatePlanListForm hotel={hotel} config={webhookConfig} />
				</Col>
			</Grid>
		</Stack>
	)
}

/**
 * The simulator's forms in a dialog, so they can be used from a hotel's
 * calendar. Same forms as the simulator page, grouped into Update ARI and
 * Fetch ARI with one form visible at a time.
 */
export function DjuboSyncAriDialog({
	hotel,
	open,
	onClose,
}: {
	hotel: IHotel
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} title="Sync ARI" lg>
			<Dialog.Body paddingX="0" paddingY="0">
				<SyncAriTabs hotel={hotel} />
			</Dialog.Body>
		</Dialog>
	)
}

function SyncAriTabs({ hotel }: { hotel: IHotel }) {
	const { webhookConfig, isFetching, error } = useDjuboIntegration()

	if (isFetching) {
		return <Text>Loading Djubo integration...</Text>
	}

	if (error || !webhookConfig) {
		return (
			<Alert status="warning" title="Djubo integration missing">
				This hotel is connected to Djubo, but there is no active Djubo
				integration for your organization. Please create one from the
				Integrations settings before syncing ARI.
			</Alert>
		)
	}

	return (
		<Tabs layout="col" defaultActive="update">
			<TabsList>
				<TabItem id="update">Update ARI</TabItem>
				<TabItem id="fetch">Fetch ARI</TabItem>
			</TabsList>
			<TabContent id="update">
				<Tabs defaultActive="update_room_rates">
					<TabsList>
						<TabItem id="update_room_rates">Update Room Rates</TabItem>
						<TabItem id="update_room_inventory">Update Room Inventory</TabItem>
						<TabItem id="stop_sell_by_rate_plan">
							Stop Sell by Rate Plan
						</TabItem>
					</TabsList>
					<TabContent id="update_room_rates">
						<UpdateRoomRatesForm hotel={hotel} config={webhookConfig} />
					</TabContent>
					<TabContent id="update_room_inventory">
						<UpdateRoomInventoryForm hotel={hotel} config={webhookConfig} />
					</TabContent>
					<TabContent id="stop_sell_by_rate_plan">
						<StopSellByRatePlanForm hotel={hotel} config={webhookConfig} />
					</TabContent>
				</Tabs>
			</TabContent>
			<TabContent id="fetch">
				<Tabs defaultActive="get_room_rates">
					<TabsList>
						<TabItem id="get_room_rates">Get Room Rates</TabItem>
						<TabItem id="get_room_inventory">Get Room Inventory</TabItem>
						<TabItem id="get_rooms_and_rate_plans">
							Get Rooms and Rate Plans
						</TabItem>
					</TabsList>
					<TabContent id="get_room_rates">
						<GetRoomRatesForm hotel={hotel} config={webhookConfig} />
					</TabContent>
					<TabContent id="get_room_inventory">
						<GetRoomInventoryForm hotel={hotel} config={webhookConfig} />
					</TabContent>
					<TabContent id="get_rooms_and_rate_plans">
						<GetRoomAndRatePlanListForm hotel={hotel} config={webhookConfig} />
					</TabContent>
				</Tabs>
			</TabContent>
		</Tabs>
	)
}

function WebhookConfigDetails({
	config,
	hotel,
}: {
	config: TDjuboWebhookConfig
	hotel: IHotel
}) {
	const [showAuth, setShowAuth] = useState(false)
	return (
		<Box padding="4" bgColor="subtle" rounded="md">
			<Stack gap="2">
				<Heading as="h5">Connection</Heading>
				<Text fontSize="sm">
					Hotel Id: <b>{hotel.id}</b>
				</Text>
				<Text fontSize="sm">
					Webhook Url: <b>{config.url}</b>
				</Text>
				<Text fontSize="sm">
					Source (Username): <b>{config.source}</b>
				</Text>
				<Inline gap="2" alignItems="center">
					<Text fontSize="sm">
						Auth (Password): <b>{showAuth ? config.auth : "•".repeat(16)}</b>
					</Text>
					<Button size="sm" onClick={() => setShowAuth((shown) => !shown)}>
						{showAuth ? "Hide" : "Show"}
					</Button>
				</Inline>
			</Stack>
		</Box>
	)
}

function WebhookResponse({
	response,
}: {
	response: TDjuboWebhookResponse | undefined
}) {
	if (!response) return null
	const body = response.body || "(empty response)"
	return (
		<Stack gap="1">
			<Text
				fontSize="sm"
				fontWeight="semibold"
				color={response.ok ? "success" : "danger"}
			>
				HTTP {response.status}
			</Text>
			<Box position="relative">
				<Box
					as="pre"
					padding="2"
					bgColor="subtle"
					rounded="md"
					fontSize="xs"
					overflow="auto"
				>
					{body}
				</Box>
				<Box position="absolute" style={{ top: "4px", right: "4px" }}>
					<CopyToClipboard>
						{({ copyTextToClipboard }) => (
							<CopyToClipboardButton
								size="sm"
								iconOnly
								title="Copy the response"
								onClick={() => copyTextToClipboard(body)}
							>
								<Icons.Duplicate />
							</CopyToClipboardButton>
						)}
					</CopyToClipboard>
				</Box>
			</Box>
		</Stack>
	)
}

function SimulatorCard({
	title,
	action,
	children,
}: {
	title: string
	action: TDjuboAction
	children: React.ReactNode
}) {
	return (
		<Box padding="6" borderWidth="1" rounded="md" bgColor="default">
			<Stack gap="4">
				<Stack gap="1">
					<Heading as="h5">{title}</Heading>
					<Text fontSize="sm" color="muted">
						actionId: {action}
					</Text>
				</Stack>
				{children}
			</Stack>
		</Box>
	)
}

function MissingOtaConfiguration({ what }: { what: string }) {
	return (
		<Alert status="warning" title={`No ${what} for this hotel`}>
			Please configure the OTA room types, meal plans, and rate plans of this
			hotel before simulating this webhook.
		</Alert>
	)
}

function DateRangeFields({ prefix }: { prefix: string }) {
	return (
		<DateRangePickerField
			label="Dates"
			fromName={`${prefix}start_date`}
			toName={`${prefix}end_date`}
			// the webhook reject past dates, so only offer forward ranges
			rangePresets={["Today", "Next 7 Days"]}
			required
		/>
	)
}

function RatePlanField({ hotel, prefix }: { hotel: IHotel; prefix: string }) {
	const ratePlans = hotel.ota_rate_plans || []
	return (
		<SelectInputField
			label="Rate Plan"
			name={`${prefix}rate_plan_code`}
			required
		>
			<option value="">Select a rate plan</option>
			{ratePlans.map((ratePlan) => (
				<option value={ratePlan.id} key={ratePlan.id}>
					{ratePlan.name} (#{ratePlan.id})
				</option>
			))}
		</SelectInputField>
	)
}

function RoomTypeField({ hotel, prefix }: { hotel: IHotel; prefix: string }) {
	const roomTypes = hotel.ota_room_types || []
	return (
		<SelectInputField label="Room Type" name={`${prefix}room_id`} required>
			<option value="">Select a room type</option>
			{roomTypes.map((roomType) => (
				<option value={roomType.id} key={roomType.id}>
					{roomType.name} (#{roomType.id})
				</option>
			))}
		</SelectInputField>
	)
}

const UPDATE_RATES_PREFIX = "update_rates_"

type TUpdateRoomRatesParams = {
	update_rates_rate_plan_code: string
	update_rates_start_date: Date
	update_rates_end_date: Date
	update_rates_weekdays: Array<TDjuboWeekday>
	update_rates_sell_rate: number
	update_rates_extra_adult: number | string
	update_rates_extra_child: number | string
	update_rates_extra_infant: number | string
}

const updateRoomRatesValidator = Validator.object().shape({
	update_rates_rate_plan_code: Validator.string().required(
		"Please select a rate plan"
	),
	update_rates_weekdays: Validator.array().min(
		1,
		"Please select at least one day"
	),
	update_rates_sell_rate: Validator.number()
		.required("Please provide a sell rate")
		.min(0, "Sell rate can not be negative"),
	update_rates_extra_adult: EmptyNumberValidator()
		.required("Please provide an extra adult rate")
		.min(0, "Rate can not be negative"),
	update_rates_extra_child: EmptyNumberValidator()
		.required("Please provide an extra child rate")
		.min(0, "Rate can not be negative"),
	update_rates_extra_infant: EmptyNumberValidator().min(
		0,
		"Rate can not be negative"
	),
	...dateRangeValidators(UPDATE_RATES_PREFIX),
})

function UpdateRoomRatesForm({
	hotel,
	config,
}: {
	hotel: IHotel
	config: TDjuboWebhookConfig
}) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TDjuboWebhookResponse>()
	const [initialValues] = useState<TUpdateRoomRatesParams>(() => ({
		update_rates_rate_plan_code: "",
		update_rates_start_date: startOf(new Date(), "day"),
		update_rates_end_date: startOf(new Date(), "day"),
		update_rates_weekdays: DJUBO_WEEKDAYS.map((d) => d.id),
		update_rates_sell_rate: 2000,
		update_rates_extra_adult: 500,
		update_rates_extra_child: 300,
		update_rates_extra_infant: "",
	}))
	if (!(hotel.ota_rate_plans || []).length) {
		return (
			<SimulatorCard
				title="Update Room Rates"
				action={DJUBO_ACTIONS.UPDATE_ROOM_RATES}
			>
				<MissingOtaConfiguration what="rate plans" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Update Room Rates"
			action={DJUBO_ACTIONS.UPDATE_ROOM_RATES}
		>
			<Form<TUpdateRoomRatesParams>
				initialValues={initialValues}
				validate={validateFormValues(updateRoomRatesValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendDjuboWebhook({
							xhr,
							config,
							action: DJUBO_ACTIONS.UPDATE_ROOM_RATES,
							requestData: {
								hotelId: hotel.id,
								ratePlanCode: Number(values.update_rates_rate_plan_code),
								startDate: toApiDate(values.update_rates_start_date),
								endDate: toApiDate(values.update_rates_end_date),
								days: weekdaysToDjuboBitmask(values.update_rates_weekdays),
								currency: hotel.currency,
								sellRate: Number(values.update_rates_sell_rate),
								extraAdult: Number(values.update_rates_extra_adult),
								extraChild: Number(values.update_rates_extra_child),
								extraInfant: Number(values.update_rates_extra_infant || 0),
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RatePlanField hotel={hotel} prefix={UPDATE_RATES_PREFIX} />
							<DateRangeFields prefix={UPDATE_RATES_PREFIX} />
							<Stack gap="1">
								<Text fontSize="sm" fontWeight="semibold">
									Days
								</Text>
								<Inline gap="4" flexWrap="wrap">
									{DJUBO_WEEKDAYS.map((day) => (
										<CheckboxInputField
											key={day.id}
											name="update_rates_weekdays"
											value={day.id}
											label={day.name}
											// the seven checkboxes share one field name, so let
											// the group report the error once below instead
											hideErrors
										/>
									))}
								</Inline>
								<ErrorMessage name="update_rates_weekdays" />
							</Stack>
							<TextInputField
								label={`Sell Rate (${hotel.currency})`}
								name="update_rates_sell_rate"
								type="number"
								min={0}
								required
							/>
							<TextInputField
								label={`Extra Adult (${hotel.currency})`}
								name="update_rates_extra_adult"
								type="number"
								min={0}
								required
							/>
							<TextInputField
								label={`Extra Child (${hotel.currency})`}
								name="update_rates_extra_child"
								type="number"
								min={0}
								required
							/>
							<TextInputField
								label={`Extra Infant (${hotel.currency})`}
								name="update_rates_extra_infant"
								type="number"
								min={0}
							/>
							<SubmissionError />
							<Divider sm />
							<Box>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Sending..." : "Send Rates"}
								</Button>
							</Box>
							<WebhookResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const UPDATE_INVENTORY_PREFIX = "update_inventory_"

type TUpdateRoomInventoryParams = {
	update_inventory_room_id: string
	update_inventory_start_date: Date
	update_inventory_end_date: Date
	update_inventory_available: number
}

const updateRoomInventoryValidator = Validator.object().shape({
	update_inventory_room_id: Validator.string().required(
		"Please select a room type"
	),
	update_inventory_available: Validator.number()
		.required("Please provide the available rooms")
		.min(0, "Available rooms can not be negative"),
	...dateRangeValidators(UPDATE_INVENTORY_PREFIX),
})

function UpdateRoomInventoryForm({
	hotel,
	config,
}: {
	hotel: IHotel
	config: TDjuboWebhookConfig
}) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TDjuboWebhookResponse>()
	const [initialValues] = useState<TUpdateRoomInventoryParams>(() => ({
		update_inventory_room_id: "",
		update_inventory_start_date: startOf(new Date(), "day"),
		update_inventory_end_date: startOf(new Date(), "day"),
		update_inventory_available: 5,
	}))
	const roomTypes = hotel.ota_room_types || []
	if (!roomTypes.length) {
		return (
			<SimulatorCard
				title="Update Room Inventory"
				action={DJUBO_ACTIONS.UPDATE_ROOM_INVENTORY}
			>
				<MissingOtaConfiguration what="room types" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Update Room Inventory"
			action={DJUBO_ACTIONS.UPDATE_ROOM_INVENTORY}
		>
			<Form<TUpdateRoomInventoryParams>
				initialValues={initialValues}
				validate={validateFormValues(updateRoomInventoryValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendDjuboWebhook({
							xhr,
							config,
							action: DJUBO_ACTIONS.UPDATE_ROOM_INVENTORY,
							requestData: {
								hotelId: hotel.id,
								roomId: Number(values.update_inventory_room_id),
								startDate: toApiDate(values.update_inventory_start_date),
								endDate: toApiDate(values.update_inventory_end_date),
								available: Number(values.update_inventory_available),
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RoomTypeField hotel={hotel} prefix={UPDATE_INVENTORY_PREFIX} />
							<DateRangeFields prefix={UPDATE_INVENTORY_PREFIX} />
							<TextInputField
								label="Available Rooms"
								name="update_inventory_available"
								type="number"
								min={0}
								required
							/>
							<SubmissionError />
							<Divider sm />
							<Box>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Sending..." : "Send Inventory"}
								</Button>
							</Box>
							<WebhookResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const STOP_SELL_PREFIX = "stop_sell_"

type TStopSellByRatePlanParams = {
	stop_sell_rate_plan_code: string
	stop_sell_start_date: Date
	stop_sell_end_date: Date
	stop_sell_flag: string
}

const stopSellByRatePlanValidator = Validator.object().shape({
	stop_sell_rate_plan_code: Validator.string().required(
		"Please select a rate plan"
	),
	...dateRangeValidators(STOP_SELL_PREFIX),
})

function StopSellByRatePlanForm({
	hotel,
	config,
}: {
	hotel: IHotel
	config: TDjuboWebhookConfig
}) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TDjuboWebhookResponse>()
	const [initialValues] = useState<TStopSellByRatePlanParams>(() => ({
		stop_sell_rate_plan_code: "",
		stop_sell_start_date: startOf(new Date(), "day"),
		stop_sell_end_date: startOf(new Date(), "day"),
		stop_sell_flag: "1",
	}))
	if (!(hotel.ota_rate_plans || []).length) {
		return (
			<SimulatorCard
				title="Stop Sell by Rate Plan"
				action={DJUBO_ACTIONS.STOP_SELL_BY_RATE_PLAN}
			>
				<MissingOtaConfiguration what="rate plans" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Stop Sell by Rate Plan"
			action={DJUBO_ACTIONS.STOP_SELL_BY_RATE_PLAN}
		>
			<Form<TStopSellByRatePlanParams>
				initialValues={initialValues}
				validate={validateFormValues(stopSellByRatePlanValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendDjuboWebhook({
							xhr,
							config,
							action: DJUBO_ACTIONS.STOP_SELL_BY_RATE_PLAN,
							requestData: {
								hotelId: hotel.id,
								ratePlanCode: Number(values.stop_sell_rate_plan_code),
								startDate: toApiDate(values.stop_sell_start_date),
								endDate: toApiDate(values.stop_sell_end_date),
								stopSell: values.stop_sell_flag === "1" ? 1 : 0,
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RatePlanField hotel={hotel} prefix={STOP_SELL_PREFIX} />
							<DateRangeFields prefix={STOP_SELL_PREFIX} />
							<SelectInputField
								label="Stop Sell"
								name="stop_sell_flag"
								required
							>
								<option value="1">Yes, stop selling</option>
								<option value="0">No, resume selling</option>
							</SelectInputField>
							<SubmissionError />
							<Divider sm />
							<Box>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Sending..." : "Send Stop Sell"}
								</Button>
							</Box>
							<WebhookResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

/**
 * The read apis answer straight away with the current data instead of queueing
 * a job, so their response pane show what Sembark would hand back to Djubo.
 */
function GetRoomAndRatePlanListForm({
	hotel,
	config,
}: {
	hotel: IHotel
	config: TDjuboWebhookConfig
}) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TDjuboWebhookResponse>()
	return (
		<SimulatorCard
			title="Get Rooms and Rate Plans"
			action={DJUBO_ACTIONS.GET_ROOM_AND_RATE_PLAN_LIST}
		>
			<Form<Record<string, never>>
				initialValues={{}}
				onSubmit={withServerErrors(async () => {
					setResponse(
						await sendDjuboWebhook({
							xhr,
							config,
							action: DJUBO_ACTIONS.GET_ROOM_AND_RATE_PLAN_LIST,
							requestData: { hotelId: hotel.id },
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<SubmissionError />
							<Divider sm />
							<Box>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Fetching..." : "Get Rooms and Rate Plans"}
								</Button>
							</Box>
							<WebhookResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const GET_RATES_PREFIX = "get_rates_"

type TGetRoomRatesParams = {
	get_rates_rate_plan_code: string
	get_rates_start_date: Date
	get_rates_end_date: Date
}

const getRoomRatesValidator = Validator.object().shape({
	get_rates_rate_plan_code: Validator.string().required(
		"Please select a rate plan"
	),
	...dateRangeValidators(GET_RATES_PREFIX),
})

function GetRoomRatesForm({
	hotel,
	config,
}: {
	hotel: IHotel
	config: TDjuboWebhookConfig
}) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TDjuboWebhookResponse>()
	const [initialValues] = useState<TGetRoomRatesParams>(() => ({
		get_rates_rate_plan_code: "",
		get_rates_start_date: startOf(new Date(), "day"),
		get_rates_end_date: startOf(new Date(), "day"),
	}))
	if (!(hotel.ota_rate_plans || []).length) {
		return (
			<SimulatorCard
				title="Get Room Rates"
				action={DJUBO_ACTIONS.GET_ROOM_RATES}
			>
				<MissingOtaConfiguration what="rate plans" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard title="Get Room Rates" action={DJUBO_ACTIONS.GET_ROOM_RATES}>
			<Form<TGetRoomRatesParams>
				initialValues={initialValues}
				validate={validateFormValues(getRoomRatesValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendDjuboWebhook({
							xhr,
							config,
							action: DJUBO_ACTIONS.GET_ROOM_RATES,
							requestData: {
								hotelId: hotel.id,
								ratePlanCode: Number(values.get_rates_rate_plan_code),
								startDate: toApiDate(values.get_rates_start_date),
								endDate: toApiDate(values.get_rates_end_date),
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RatePlanField hotel={hotel} prefix={GET_RATES_PREFIX} />
							<DateRangeFields prefix={GET_RATES_PREFIX} />
							<SubmissionError />
							<Divider sm />
							<Box>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Fetching..." : "Get Rates"}
								</Button>
							</Box>
							<WebhookResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const GET_INVENTORY_PREFIX = "get_inventory_"

type TGetRoomInventoryParams = {
	get_inventory_room_id: string
	get_inventory_start_date: Date
	get_inventory_end_date: Date
}

const getRoomInventoryValidator = Validator.object().shape({
	get_inventory_room_id: Validator.string().required(
		"Please select a room type"
	),
	...dateRangeValidators(GET_INVENTORY_PREFIX),
})

function GetRoomInventoryForm({
	hotel,
	config,
}: {
	hotel: IHotel
	config: TDjuboWebhookConfig
}) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TDjuboWebhookResponse>()
	const [initialValues] = useState<TGetRoomInventoryParams>(() => ({
		get_inventory_room_id: "",
		get_inventory_start_date: startOf(new Date(), "day"),
		get_inventory_end_date: startOf(new Date(), "day"),
	}))
	if (!(hotel.ota_room_types || []).length) {
		return (
			<SimulatorCard
				title="Get Room Inventory"
				action={DJUBO_ACTIONS.GET_ROOM_INVENTORY}
			>
				<MissingOtaConfiguration what="room types" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Get Room Inventory"
			action={DJUBO_ACTIONS.GET_ROOM_INVENTORY}
		>
			<Form<TGetRoomInventoryParams>
				initialValues={initialValues}
				validate={validateFormValues(getRoomInventoryValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendDjuboWebhook({
							xhr,
							config,
							action: DJUBO_ACTIONS.GET_ROOM_INVENTORY,
							requestData: {
								hotelId: hotel.id,
								roomId: Number(values.get_inventory_room_id),
								startDate: toApiDate(values.get_inventory_start_date),
								endDate: toApiDate(values.get_inventory_end_date),
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RoomTypeField hotel={hotel} prefix={GET_INVENTORY_PREFIX} />
							<DateRangeFields prefix={GET_INVENTORY_PREFIX} />
							<SubmissionError />
							<Divider sm />
							<Box>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Fetching..." : "Get Inventory"}
								</Button>
							</Box>
							<WebhookResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}
